import { combineReducers } from 'redux';
import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import AuthStateReducer, { AuthState } from './authentication/Reducer';
import BaseAppStateReducer, {
  BaseApplicationState,
} from './baseapplication/Reducer';
import ProfileStateReducer, { ProfileState } from './profile/Reducer';
import CarriesAppStateReducer from './carriesapplication/Reducer';
import PersistAppStateReducer from './persistapplication/Reducer';
import { createReduxHistoryContext } from 'redux-first-history';
import storageSession from 'redux-persist/lib/storage/session';
import { createBrowserHistory } from 'history';
import {
  targetFormikListener,
  useFormikEffectListener,
  formikReducer,
} from 'shared/helpers/Formik';
import {
  createStateSyncMiddleware,
  initMessageListener,
} from 'redux-state-sync';

import { AuthReduxActions } from './authentication/Action';
import PublicAuthStateReducer from './public/Reducer';
import PaginationReducer, { PaginationState } from './pagination/Reducer';

const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({
    history: createBrowserHistory({ window }),
    savePreviousLocations: 1,
  });

const routePersistConfig = {
  key: 'router',
  storage: storageSession,
  blacklist: [],
};

export interface IAppState {
  authState: AuthState;
  baseAppState: BaseApplicationState;
  profileState: ProfileState;
  paginationState: PaginationState;
}

const appReducer = combineReducers({
  router: persistReducer(routePersistConfig, routerReducer),
  authState: AuthStateReducer,
  baseAppState: BaseAppStateReducer,
  profileState: ProfileStateReducer,
  formikState: formikReducer,
  carryState: CarriesAppStateReducer,
  persistState: PersistAppStateReducer,
  publicState: PublicAuthStateReducer,
  paginationState: PaginationReducer,
});

const rootReducer = (state: any | undefined, action: any): any => {
  if (action.type === 'LOGOUT' || action.type === 'RESET_STORE') {
    storage.removeItem('persist:clarity');
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const persistConfig = {
  key: 'clarity',
  storage,
  stateReconciler: autoMergeLevel2,
  blacklist: ['carryState', 'router'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);
const listenerMiddleware = createListenerMiddleware();
listenerMiddleware.startListening({
  type: targetFormikListener,
  effect: useFormikEffectListener,
});

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
      thunk: true,
    })
      .concat(
        createStateSyncMiddleware({
          whitelist: [AuthReduxActions.LOGOUT],
          broadcastChannelOption: { type: 'localstorage' },
        }) as any
      )
      .concat(routerMiddleware)
      .prepend(listenerMiddleware.middleware),
  devTools: process.env.NODE_ENV !== 'production',
});
initMessageListener(store);
export const history = createReduxHistory(store);
export const persistor = persistStore(store);
export default store;
